import React from 'react';
import BadgeAvatar from '../components/BadgeAvatar';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const CreateHub = ({ user, logout }) => {
  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Header */}
      <header className='bg-white shadow-sm border-b border-gray-200'>
        <div className='container mx-auto px-4 py-4'>
          <div className='flex items-center justify-between'>
            <Link to='/dashboard' className='flex items-center space-x-3'>
              <div className='w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center'>
                <svg className='w-6 h-6 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' />
                </svg>
              </div>
              <span className='text-xl font-bold text-gray-900' style={{ fontFamily: 'Playfair Display, serif' }}>TCM Study Club</span>
            </Link>
            <div className='flex items-center space-x-4'>
              <Link to={`/profile/${user.id}`}>
                <img src={user.picture || 'https://via.placeholder.com/40'} alt={user.name} className='w-10 h-10 rounded-full border-2 border-teal-500' />
              </Link>
              <Button onClick={logout} variant='outline' className='border-gray-300 text-gray-700 hover:bg-gray-100 rounded-full px-6'>Logout</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className='container mx-auto px-4 py-8 max-w-7xl'>
        <div className='bg-white rounded-3xl p-8 shadow-lg'>
          <div className='text-center mb-12'>
            <h1 className='text-4xl font-bold text-gray-900 mb-4' style={{ fontFamily: 'Playfair Display, serif' }}>
              What would you like to create?
            </h1>
            <p className='text-gray-600'>Choose the type of content you want to add to TCM Study Club</p>
          </div>

          <div className={`grid ${user.is_admin ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-1'} gap-8 ${user.is_admin ? 'max-w-7xl' : 'max-w-md'} mx-auto`}>
            {/* Case Study Option */}
            <Link to='/create/case-study' data-testid='create-case-study-option'>
              <div className='bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 border-2 border-teal-200 hover:border-teal-400 hover:shadow-xl transition-all duration-300 cursor-pointer h-full'>
                <div className='w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center mb-4 mx-auto'>
                  <svg className='w-8 h-8 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' />
                  </svg>
                </div>
                <h3 className='text-xl font-bold text-gray-900 text-center mb-3'>Case Study</h3>
                <p className='text-gray-600 text-center text-sm mb-4'>
                  Create educational case studies for student learning and discussion
                </p>
                <ul className='text-xs text-gray-600 space-y-2'>
                  <li className='flex items-start'>
                    <svg className='w-4 h-4 text-teal-500 mr-2 flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                    {user.is_admin ? 'Manual entry or AI-powered generation' : 'Manual entry'}
                  </li>
                  <li className='flex items-start'>
                    <svg className='w-4 h-4 text-teal-500 mr-2 flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                    Discussion forums and comments
                  </li>
                  <li className='flex items-start'>
                    <svg className='w-4 h-4 text-teal-500 mr-2 flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                    Timed solution reveals
                  </li>
                </ul>
                <div className='mt-6 text-center'>
                  <div className='inline-block bg-white px-4 py-2 rounded-full text-sm font-medium text-teal-600 border border-teal-200'>
                    Create Case Study →
                  </div>
                </div>
              </div>
            </Link>

            {/* Simple Test Option - Admin Only */}
            {user.is_admin && (
              <Link to='/create/test/simple' data-testid='create-simple-test-option'>
              <div className='bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200 hover:border-green-400 hover:shadow-xl transition-all duration-300 cursor-pointer h-full'>
                <div className='w-16 h-16 rounded-xl flex items-center justify-center mb-4 mx-auto difficulty-simple border-0'>
                  <svg className='w-8 h-8 text-green-800' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                  </svg>
                </div>
                <h3 className='text-xl font-bold text-gray-900 text-center mb-3'>Simple Test</h3>
                <p className='text-gray-600 text-center text-sm mb-4'>
                  Create board-style competency tests for exam preparation
                </p>
                <ul className='text-xs text-gray-600 space-y-2'>
                  <li className='flex items-start'>
                    <svg className='w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                    6 multiple choice questions
                  </li>
                  <li className='flex items-start'>
                    <svg className='w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                    Timed testing with auto-scoring
                  </li>
                  <li className='flex items-start'>
                    <svg className='w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                    AI-powered test generation
                  </li>
                </ul>
                <div className='mt-6 text-center'>
                  <div className='inline-block bg-white px-4 py-2 rounded-full text-sm font-medium text-green-700 border border-green-200'>
                    Create Simple Test →
                  </div>
                </div>
              </div>
            </Link>
            )}

            {/* Complex Test Option - Admin Only */}
            {user.is_admin && (
              <Link to='/create/test/complex' data-testid='create-complex-test-option'>
              <div className='bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 border-2 border-red-200 hover:border-red-400 hover:shadow-xl transition-all duration-300 cursor-pointer h-full'>
                <div className='w-16 h-16 rounded-xl flex items-center justify-center mb-4 mx-auto difficulty-complex border-0'>
                  <svg className='w-8 h-8 text-red-800' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' />
                  </svg>
                </div>
                <h3 className='text-xl font-bold text-gray-900 text-center mb-3'>Complex Test</h3>
                <p className='text-gray-600 text-center text-sm mb-4'>
                  Create advanced clinic-ready competency assessments
                </p>
                <ul className='text-xs text-gray-600 space-y-2'>
                  <li className='flex items-start'>
                    <svg className='w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                    Advanced clinical scenarios
                  </li>
                  <li className='flex items-start'>
                    <svg className='w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                    Multiple pattern overlaps
                  </li>
                  <li className='flex items-start'>
                    <svg className='w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                    AI-powered test generation
                  </li>
                </ul>
                <div className='mt-6 text-center'>
                  <div className='inline-block bg-white px-4 py-2 rounded-full text-sm font-medium text-red-700 border border-red-200'>
                    Create Complex Test →
                  </div>
                </div>
              </div>
            </Link>
            )}

            {/* Mock Exam Option - Admin Only */}
            {user.is_admin && (
              <Link to='/create/mock-exam' data-testid='create-mock-exam-option'>
              <div className='bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border-2 border-purple-200 hover:border-purple-400 hover:shadow-xl transition-all duration-300 cursor-pointer h-full'>
                <div className='w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4 mx-auto'>
                  <svg className='w-8 h-8 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                  </svg>
                </div>
                <h3 className='text-xl font-bold text-gray-900 text-center mb-3'>Case Study Mock Exam</h3>
                <p className='text-gray-600 text-center text-sm mb-4'>
                  Create comprehensive 90-question mock exam from 15 diagnoses
                </p>
                <ul className='text-xs text-gray-600 space-y-2'>
                  <li className='flex items-start'>
                    <svg className='w-4 h-4 text-purple-500 mr-2 flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                    90 questions (6 per diagnosis)
                  </li>
                  <li className='flex items-start'>
                    <svg className='w-4 h-4 text-purple-500 mr-2 flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                    Detailed explanations for answers
                  </li>
                  <li className='flex items-start'>
                    <svg className='w-4 h-4 text-purple-500 mr-2 flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                    </svg>
                    AI-powered comprehensive exam
                  </li>
                </ul>
                <div className='mt-6 text-center'>
                  <div className='inline-block bg-white px-4 py-2 rounded-full text-sm font-medium text-purple-700 border border-purple-200'>
                    Create Mock Exam →
                  </div>
                </div>
              </div>
            </Link>
            )}
          </div>

          <div className='mt-8 text-center'>
            <Link to='/dashboard'>
              <Button variant='outline' className='rounded-full px-8'>
                Cancel
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateHub;
